import {useEffect} from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsersRectangle, faCircleInfo} from '@fortawesome/pro-light-svg-icons';
import { useFormContext } from 'react-hook-form';
import {useCustomerContext} from '../../lib/context/customerContext';
import {useFeatureContext} from '../../lib/context/featureContext';
import {MinimumPolicyDisplay} from './minimumPolicyDisplay';
import Popover from '../popover';
import variables from '../../styles/_variables.module.scss';

export const PolicyForm = ({
  minPolicy,
  showAllFormElements = true,
  clicked = '',
  rca = true,
  existsError = "",
  setExistsError = () => {},
  thresholdError = "",
  setThresholdError = () => {},
  setMinimumError = () => {},
  defaultExistsError = "",
  setDefaultExistsError = () => {}}) => {
  
  const {
    register,
    setFocus,
    watch,
    formState: {errors},
    trigger
  } = useFormContext();
  
  const {hasPermission} = useCustomerContext();
  const {feature_customer_mdm, feature_customer_edge_lock} = useFeatureContext();
  const editingMinimumPolicy = clicked === 'minpolicy';
  const mdmActionDisabled = !hasPermission('POLICY_MDM_ACTION_EDIT');
  const edgeLockDisabled = !hasPermission('POLICY_EDGELOCK_EDIT');

  useEffect(() => {
    if (clicked !== 'createpolicy' && clicked !== 'minpolicy' ) {
      trigger()
    }
  },[clicked]);

  useEffect(() => {
    setMinimumError(Boolean(
      errors?.is_cellular_connected ||
      errors?.has_battery_warning ||
      errors?.battery_warning_threshold
    ));
  }, [
    errors?.is_cellular_connected,
    errors?.has_battery_warning,
    errors?.battery_warning_threshold,
    setMinimumError
  ]);

  const profilePopoverContent = () => {
    return (
      <>
        <p><strong>Strict Mode:</strong> Enforces full and immediate compliance with policy. All faults or policy issues are violations.</p>
        <p className="mb-0"><strong>Standard Mode:</strong> Select faults or policy conditions are issued as warnings, allowing correction before they result in violations.</p>
      </>
    );
  }

  const mdmPopoverContent = () => {
    return (
      <>
        <p className="fw-bold">Default MDM Action</p>
        <p><strong>CRBN (iOS):</strong> MDM Lost Mode with a custom message is triggered</p>
        <p className="mb-0"><strong>CRBN X (Android):</strong> MDM Lost Phone Sound is triggered</p>
      </>
    );
  }

  return (
    <div className="offcanvas-form" data-testid="offcanvas-policy">
      <Row>
        <Col>

        {showAllFormElements && 
          <Form.Group className="mb-3">
            <Form.Label>Policy Name</Form.Label>
            <Form.Control data-testid="policy-name-text" 
              {...register("name", {
              onChange: () => setExistsError(null),
              required: "Policy Name is required.",
              pattern: {
                value: /^[a-zA-Z0-9-_.]+$/,
                message: "Invalid Policy Name - Letters, numbers, hyphens, underscores, and periods are acceptable."
              },
              maxLength: {
                value: 50,
                message: "Length of Policy Name exceeds 50 characters."
              }})}
              type="text"
              placeholder="Policy Name"
              className={cx({'error-input': errors?.name || existsError})}
            />
          </Form.Group>
        }

        {showAllFormElements && 
          <MinimumPolicyDisplay minPolicy={minPolicy} errors={errors} />
        }
        
          <Form.Group className="mb-3">
            <Form.Label className="mb-3">Policy Rules</Form.Label>

            <div className="check-container p-3">
              <div className="mb-2">
                <Form.Check 
                  {...register("is_phone_protected")}
                  data-testid="protected-mode-checkbox"
                  type="switch"
                  name="is_phone_protected"
                  id="is_phone_protected"
                  label="Protected Mode ON"
                  disabled
                  checked
                />
              </div>

              {showAllFormElements &&
              <Row className="mb-2 ms-4">
                <Col sm="4" className="align-self-end">
                  <Form.Label className="text-nowrap">Profile:</Form.Label>
                </Col>
                <Col sm="6" className="align-self-end">
                  <Form.Select
                    {...register("protected_profile")}>
                    <option key={0} value={"secure"}>Strict Mode</option>
                    <option key={1} value={"basic"}>Standard Mode</option>
                  </Form.Select>
                </Col>
                <Col sm="2" className="align-self-center">
                  <Popover placement="left" content={profilePopoverContent}>
                    <FontAwesomeIcon className="align-middle" size="lg" icon={faCircleInfo} color={variables.privoroBlue}/>
                  </Popover>
                </Col>
              </Row>
              }

              {feature_customer_mdm && showAllFormElements &&
                <Row className="mb-2 ms-4 align-items-end">
                  <Col sm="4">
                    <Form.Label className="text-nowrap">MDM Action: </Form.Label>
                  </Col>
                  <Col sm="6">
                    <Form.Select
                      {...register("policy_action.antisurveillance_mdm_action", {
                        valueAsNumber: true,
                        required: mdmActionDisabled ? false : "Please select an option."
                      })}
                      disabled={mdmActionDisabled}
                      className={cx({'error-input': errors?.policy_action?.antisurveillance_mdm_action})}
                    >
                      <option key={0} value={0}>Disabled</option>
                      <option key={1} value={1}>Default</option>
                    </Form.Select>
                  </Col>
                  <Col sm="2" className="align-self-center">
                    <Popover placement="left" content={mdmPopoverContent}>
                      <FontAwesomeIcon className="align-middle" size="lg" icon={faCircleInfo} color={variables.privoroBlue}/>
                    </Popover>
                  </Col>
                </Row>
              }

              {feature_customer_edge_lock && showAllFormElements &&
              <Row className="mb-2 ms-4 align-items-end">
                <Col sm="4">
                  <Form.Label className="text-nowrap">Edge Locking:</Form.Label>
                </Col>
                <Col sm="6">
                  <Form.Select
                    {...register("policy_action.antisurveillance_edge_action", {
                      valueAsNumber: true,
                      required: edgeLockDisabled ? false : "Please select an option."
                    })}
                    disabled={edgeLockDisabled}
                    className={cx({'error-input': errors?.policy_action?.antisurveillance_edge_action})}>
                    <option key={0} value={0}>Disabled</option>
                    <option key={1} value={1}>Enabled</option>
                  </Form.Select>
                </Col>
              </Row>
            }

              <div className={cx('mb-2 mt-3', {'mt-5': showAllFormElements})}>
                <Form.Check 
                  {...register("is_cellular_connected", {
                    validate: (v) => {
                      if (!editingMinimumPolicy && minPolicy?.is_cellular_connected && !v) {
                        return "error";
                      }
                      return true;
                    }
                  })}
                  data-testid="cellular-switch"
                  type="switch"
                  id="cellular"
                  label="Cellular Radio OFF"
                  disabled={clicked === 'minpolicy' && !rca}
                  className={cx({'error-input': errors?.is_cellular_connected || existsError})}
                />
              </div>



              {feature_customer_mdm && showAllFormElements &&
                <Row className={cx('mb-2 ms-4 align-items-end', {'d-none': watch('is_cellular_connected') !== true})}>
                  <Col sm="4">
                    <Form.Label className="text-nowrap">MDM Action:</Form.Label>
                  </Col>
                  <Col sm="6">
                    <Form.Select
                      {...register("policy_action.cellular_mdm_action", {
                        valueAsNumber: true,
                        required: mdmActionDisabled ? false : "Please select an option."
                      })}
                      disabled={mdmActionDisabled}
                      className={cx({'error-input': errors?.policy_action?.cellular_mdm_action})}
                    >
                      <option key={0} value={0}>Disabled</option>
                      <option key={1} value={1}>Default</option>
                    </Form.Select>
                  </Col>
                </Row>
              }

              {feature_customer_edge_lock && showAllFormElements &&
              <Row className={cx('mb-2 ms-4 align-items-end', {'d-none': watch('is_cellular_connected') !== true})}>
                <Col sm="4">
                  <Form.Label className="text-nowrap">Edge Locking:</Form.Label>
                </Col>
                <Col sm="6">
                  <Form.Select
                    {...register("policy_action.cellular_edge_action", {
                      valueAsNumber: true,
                      required: edgeLockDisabled ? false : "Please select an option."
                    })}
                    disabled={edgeLockDisabled}
                    className={cx({'error-input': errors?.policy_action?.cellular_edge_action})}>
                    <option key={0} value={0}>Disabled</option>
                    <option key={1} value={1}>Enabled</option>
                  </Form.Select>
                </Col>
              </Row>
            }

              <div className={cx('mb-2 mt-3', {'mt-5': showAllFormElements})}>
                <Form.Check 
                  {...register("has_battery_warning", {
                    onChange: (e) => {
                      if (e.target.checked) setFocus('battery_warning_threshold');
                    },
                    validate: (v) => {
                      if (!editingMinimumPolicy && minPolicy?.has_battery_warning && !v) {
                          return "error";
                      }
                      return true;
                    }
                  })}
                  data-testid="battery-level-checkbox"
                  type="switch"
                  id="rule-battery"
                  label="Minimum Battery Level"
                  defaultChecked={false}
                  disabled={clicked === 'minpolicy' && !rca}
                  className={cx({'error-input': errors?.has_battery_warning})}
                />
              </div>

              <Row className={cx('mb-2 ms-4 align-items-end', {'d-none': watch('has_battery_warning') !== true})}>
                <Col sm="4">
                  <Form.Label>Above:</Form.Label>
                </Col>
                <Col sm="6">
                  <Form.Select
                    data-testid="threshold-combobox"
                    disabled={!watch('has_battery_warning') || (clicked === 'minpolicy' && !rca)}
                    className={cx({'error-input': thresholdError || errors?.battery_warning_threshold})}
                    {...register("battery_warning_threshold", {
                      valueAsNumber: true,
                      validate: (v) => {
                        if (!editingMinimumPolicy && minPolicy?.has_battery_warning && v < minPolicy.battery_warning_threshold) {
                          return "error";
                        }
                        setThresholdError(null)
                        return true;
                      }
                    })}
                  >
                    {(() => {
                      const options = [];
                      options.push(<option key='' value='0'></option>);
                      for (let i = 10; i <= 50; i+=10) {
                        options.push(<option key={i} value={i}>{i}%</option>);
                      }
                      return options;
                    })()}
                  </Form.Select>
                </Col>
              </Row>

              {feature_customer_mdm && showAllFormElements &&
                <Row className={cx('mb-2 ms-4 align-items-end', {'d-none': watch('has_battery_warning') !== true})}>
                  <Col sm="4">
                    <Form.Label className="text-nowrap">MDM Action:</Form.Label>
                  </Col>
                  <Col sm="6">
                    <Form.Select
                      {...register("policy_action.low_battery_mdm_action", {
                        valueAsNumber: true,
                        required: mdmActionDisabled ? false : "Please select an option."
                      })}
                      disabled={mdmActionDisabled}
                      className={cx({'error-input': errors?.policy_action?.low_battery_mdm_action})}
                    >
                      <option key={0} value={0}>Disabled</option>
                      <option key={1} value={1}>Default</option>
                    </Form.Select>
                  </Col>
                </Row>
              }

              {feature_customer_edge_lock && showAllFormElements &&
              <Row className={cx('ms-4 align-items-end', {'d-none': watch('has_battery_warning') !== true})}>
                <Col sm="4">
                  <Form.Label className="text-nowrap">Edge Locking:</Form.Label>
                </Col>
                <Col sm="6">
                  <Form.Select
                    {...register("policy_action.low_battery_edge_action", {
                      valueAsNumber: true,
                      required: edgeLockDisabled ? false : "Please select an option."
                    })}
                    disabled={edgeLockDisabled}
                    className={cx({'error-input': errors?.policy_action?.low_battery_edge_action})}>
                    <option key={0} value={0}>Disabled</option>
                    <option key={1} value={1}>Enabled</option>
                  </Form.Select>
                </Col>
              </Row>
            }

            {showAllFormElements &&
              <div className="mb-2 mt-5">
                <Form.Check
                  {...register("is_phone_mismatch")}
                  type="switch"
                  id="mismatch"
                  label="Phone Linking Required"
                />
              </div>
            }
            </div>  
          </Form.Group>
          
          {showAllFormElements &&
            <Form.Group className="check-container p-3">
              <div className={cx({'error-input': defaultExistsError})}>
                <FontAwesomeIcon className="ms-3 float-end" icon={faUsersRectangle} size="lg" />
                <Form.Check
                  {...register("is_default_visitor", {
                    onChange: () => setDefaultExistsError(null),
                  })}
                  type="switch"
                  id="dvp"
                  className={cx({'error-input': defaultExistsError}, 'd-inline pointer')}
                />
                <Form.Label htmlFor="dvp" className="ms-3 pointer">Default Visitor Policy</Form.Label>
                <p className="mt-3">Selecting this option sets the default policy for all visiting SafeCases.</p>
              </div>
            </Form.Group>
          }

        </Col>
      </Row>
    </div>
  )
};

export default PolicyForm;
